import Head from 'next/head'
import Script from 'next/script'
import { getArticleDateTime } from '../../../utils/article-date-time'
import { CorporateTraining } from '../../../components/index/service-category/personal-trainer/CorporateTraining'

export async function getStaticProps() {
    const { modifiedDate, month, year } = getArticleDateTime()

    return {
        props: {
            modifiedDate,
            month,
            year,
        },
        revalidate: 259200,
    }
}

export default function CorporateTrainingPage(props) {
    const { modifiedDate } = props

    return (
        <>
            <Head>
                <title>Corporate Wellness Programs Singapore | StringsSG</title>
                <meta charSet="utf-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta httpEquiv="content-language" content="en-sg" />
                <meta
                    name="description"
                    content="Corporate wellness programs in Singapore powered by expert trainers and a digital transformation platform. Trusted by Google, Amazon, JPMorgan Chase, Citigroup & GE. Plans from $2,500/month."
                />
                <meta
                    name="keywords"
                    content="corporate wellness program singapore, corporate fitness training, employee wellness program, corporate gym trainer, office fitness sessions, workplace wellness, corporate health program singapore, group fitness corporate"
                />
                <meta name="author" content="StringsSG" />
                <meta name="geo.region" content="SG" />
                <meta name="geo.placename" content="Singapore" />
                <meta name="geo.position" content="1.3521;103.8198" />
                <meta name="ICBM" content="1.3521, 103.8198" />
                <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />

                {/* Open Graph */}
                <meta property="og:locale" content="en_SG" />
                <meta property="og:locale:alternate" content="en_US" />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="og:url" content="https://www.stringssg.com/sg/personal-trainer/corporate-training" />
                <meta property="og:type" content="article" />
                <meta property="og:title" content="Corporate Wellness Programs Singapore | StringsSG" />
                <meta
                    property="og:description"
                    content="Corporate wellness programs in Singapore powered by expert trainers and a digital transformation platform. Trusted by Google, Amazon, JPMorgan Chase, Citigroup & GE."
                />
                <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
                <meta property="article:published_time" content="2026-03-16T07:30:46+00:00" />
                <meta property="article:modified_time" content={modifiedDate} />
                <meta
                    property="og:image"
                    content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg"
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@stringssg" />
                <meta name="twitter:creator" content="@stringssg" />
                <meta name="twitter:url" content="https://www.stringssg.com/sg/personal-trainer/corporate-training" />
                <meta name="twitter:title" content="Corporate Wellness Programs Singapore | StringsSG" />
                <meta
                    name="twitter:description"
                    content="Corporate wellness programs in Singapore powered by expert trainers and a digital transformation platform. Trusted by Google, Amazon, JPMorgan Chase, Citigroup & GE."
                />
                <meta
                    name="twitter:image"
                    content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg"
                />

                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/corporate-training" />
                <link rel="alternate" hrefLang="en-sg" href="https://www.stringssg.com/sg/personal-trainer/corporate-training" />
                <link rel="alternate" hrefLang="en" href="https://www.stringssg.com/sg/personal-trainer/corporate-training" />
                <link rel="alternate" hrefLang="x-default" href="https://www.stringssg.com/sg/personal-trainer/corporate-training" />

                {/* Structured Data */}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@graph": [
                                {
                                    "@type": "BreadcrumbList",
                                    "@id": "https://www.stringssg.com/sg/personal-trainer/corporate-training/#breadcrumblist",
                                    itemListElement: [
                                        {
                                            "@type": "ListItem",
                                            position: 1,
                                            item: {
                                                "@type": "WebPage",
                                                "@id": "https://www.stringssg.com/",
                                                name: "Home",
                                                url: "https://www.stringssg.com/",
                                            },
                                        },
                                        {
                                            "@type": "ListItem",
                                            position: 2,
                                            item: {
                                                "@type": "WebPage",
                                                "@id": "https://www.stringssg.com/sg/personal-trainer/",
                                                name: "Personal Trainer Singapore",
                                                url: "https://www.stringssg.com/sg/personal-trainer/",
                                            },
                                        },
                                        {
                                            "@type": "ListItem",
                                            position: 3,
                                            item: {
                                                "@type": "WebPage",
                                                "@id": "https://www.stringssg.com/sg/personal-trainer/corporate-training",
                                                name: "Corporate Wellness Programs",
                                                url: "https://www.stringssg.com/sg/personal-trainer/corporate-training",
                                            },
                                        },
                                    ],
                                },
                                {
                                    "@type": "Service",
                                    name: "Corporate Wellness Programs Singapore",
                                    description:
                                        "Comprehensive corporate wellness programs combining expert trainers and a digital transformation platform. Group fitness sessions, employee wellness app, transformation challenges, and HR analytics.",
                                    provider: {
                                        "@type": "Organization",
                                        name: "StringsSG",
                                        url: "https://www.stringssg.com",
                                        sameAs: [
                                            "https://www.facebook.com/StringsSG",
                                            "https://www.linkedin.com/company/stringssg/",
                                        ],
                                    },
                                    areaServed: {
                                        "@type": "Country",
                                        name: "Singapore",
                                    },
                                    url: "https://www.stringssg.com/sg/personal-trainer/corporate-training",
                                    hasOfferCatalog: {
                                        "@type": "OfferCatalog",
                                        name: "Corporate Wellness Plans",
                                        itemListElement: [
                                            {
                                                "@type": "Offer",
                                                itemOffered: {
                                                    "@type": "Service",
                                                    name: "Starter Wellness Program",
                                                    description:
                                                        "Weekly group fitness session, employee fitness app access, monthly fitness challenges, employee leaderboard, and quarterly wellness reports for up to 80 employees.",
                                                },
                                                price: "2500",
                                                priceCurrency: "SGD",
                                                unitText: "month",
                                            },
                                            {
                                                "@type": "Offer",
                                                itemOffered: {
                                                    "@type": "Service",
                                                    name: "Growth Wellness Program",
                                                    description:
                                                        "2-3 weekly group fitness sessions, monthly wellness workshops, corporate transformation challenges, advanced app features, and corporate wellness analytics for up to 150 employees.",
                                                },
                                                price: "4500",
                                                priceCurrency: "SGD",
                                                unitText: "month",
                                            },
                                            {
                                                "@type": "Offer",
                                                itemOffered: {
                                                    "@type": "Service",
                                                    name: "Premium Corporate Wellness",
                                                    description:
                                                        "4-5 weekly group fitness sessions, unlimited employee app access, monthly wellness workshops, company-wide transformation challenges, executive fitness sessions, and comprehensive HR wellness reports for up to 300 employees.",
                                                },
                                                price: "7000",
                                                priceCurrency: "SGD",
                                                unitText: "month",
                                            },
                                        ],
                                    },
                                },
                                {
                                    "@type": "WebPage",
                                    "@id": "https://www.stringssg.com/sg/personal-trainer/corporate-training/#webpage",
                                    url: "https://www.stringssg.com/sg/personal-trainer/corporate-training",
                                    name: "Corporate Wellness Programs Singapore | StringsSG",
                                    description:
                                        "Corporate wellness programs in Singapore powered by expert trainers and a digital transformation platform. Trusted by Google, Amazon, JPMorgan Chase, Citigroup & GE.",
                                    inLanguage: "en-US",
                                    isPartOf: { "@id": "https://www.stringssg.com/#website" },
                                    breadcrumb: {
                                        "@id": "https://www.stringssg.com/sg/personal-trainer/corporate-training/#breadcrumblist",
                                    },
                                    datePublished: "2026-03-16T07:30:46+00:00",
                                    dateModified: modifiedDate,
                                },
                            ],
                        }),
                    }}
                />
            </Head>

            <div>
                <CorporateTraining />
            </div>

            <Script
                id="google-tagmanager-new"
                src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
                strategy="lazyOnload"
                onLoad={() => {
                    window.dataLayer = window.dataLayer || []
                    function gtag() {
                        dataLayer.push(arguments)
                    }
                    gtag('js', new Date())
                    gtag('config', 'G-QD1HGGHDYJ')
                }}
            />
            <Script id="facebook-tracking" strategy="lazyOnload">
                {`!function(f,b,e,v,n,t,s)
                    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                    n.queue=[];t=b.createElement(e);t.async=!0;
                    t.src=v;s=b.getElementsByTagName(e)[0];
                    s.parentNode.insertBefore(t,s)}(window, document,'script',
                    'https://connect.facebook.net/en_US/fbevents.js');
                    fbq('init', '128634041116395');
                    fbq('track', 'PageView');`}
            </Script>
        </>
    )
}
